import { TARIFF_COMMENT_MAX_LENGTH } from '@billing/validation/comment';
import {
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { buttonTextColor } from 'utils/buttonUtils';
import type { RowItem } from './utils/tariffHelpers';

interface TariffCommentModalProps {
  row: RowItem;
  onClose: () => void;
  onSave: (comment: string) => void;
}

export const TariffCommentModal = ({
  row,
  onClose,
  onSave,
}: TariffCommentModalProps): ReactElement => {
  const { translate } = useTranslation();
  const colorScheme = useComputedColorScheme('dark');
  const [comment, setComment] = useState(row.pendingComment ?? '');
  const remaining = TARIFF_COMMENT_MAX_LENGTH - comment.length;
  const save = () => {
    if (remaining < 0) return;
    onSave(comment.trim());
  };

  return (
    <Modal
      opened
      onClose={onClose}
      centered
      size="lg"
      radius="lg"
      title={translate(
        'pages:claims.tariffComment.title',
        { code: `${row.prefix}${row.code}` },
        `Comment on tariff ${row.prefix}${row.code}`
      )}
    >
      <Stack gap="md">
        {row.latestComment && (
          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              {translate(
                'pages:claims.tariffComment.previous',
                'Latest saved comment'
              )}
            </Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {row.latestComment}
            </Text>
          </Stack>
        )}
        <Textarea
          data-autofocus
          label={translate(
            'pages:claims.tariffComment.open',
            'Comment to Provider'
          )}
          placeholder={translate(
            'pages:claims.tariffComment.placeholder',
            'Enter a comment about this tariff'
          )}
          value={comment}
          onChange={(event) => setComment(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
              event.preventDefault();
              event.stopPropagation();
              save();
            }
          }}
          autosize
          minRows={4}
          error={
            remaining < 0
              ? translate(
                  'pages:claims.tariffComment.tooLong',
                  'Use 500 characters or fewer.'
                )
              : undefined
          }
        />
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {translate(
              'pages:claims.tariffComment.help',
              'Visible to the provider on this tariff. Posted when you save the claim.'
            )}
          </Text>
          <Text size="xs" c={remaining < 0 ? 'red' : 'dimmed'}>
            {translate(
              'pages:claims.tariffComment.remaining',
              { count: remaining },
              `${remaining} characters left`
            )}
          </Text>
        </Group>
        <Group justify="flex-end">
          <Button
            color="gray"
            c={buttonTextColor(colorScheme, false)}
            variant="outline"
            onClick={onClose}
          >
            {translate('pages:claims.tariffComment.cancel', 'Cancel')}
          </Button>
          <Button
            color="green"
            c={buttonTextColor(colorScheme, remaining < 0)}
            disabled={remaining < 0}
            onClick={save}
          >
            {translate('pages:claims.tariffComment.confirm', 'Confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
